import { z } from 'zod'
import { warehouseVersion } from './warehouse.ts'

const id = z.string().trim().min(1).max(200)
const nullableText = z.string().trim().max(10_000).nullable()
const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/
/** One address, or several separated by a comma, a semicolon or a space. */
export const isEmailList = (value: string) =>
  value
    .split(/[,;\s]+/)
    .filter(Boolean)
    .every((address) => EMAIL.test(address))
const emailList = z.string().trim().min(1).max(1000).refine(isEmailList).nullable()
const contactFields = z
  .object({
    fullName: z.string().trim().min(1).max(300),
    role: z.string().trim().min(1).max(300),
    phone: z.string().trim().min(1).max(100).nullable(),
    email: emailList,
    note: nullableText,
  })
  .strict()

const supplierFields = z
  .object({
    name: z.string().trim().min(1).max(500),
    edrpou: z
      .string()
      .regex(/^\d{8,10}$/)
      .nullable(),
    city: z.string().trim().min(1),
    address: z.string().nullable(),
    contactName: z.string().nullable(),
    phone: z.string().nullable(),
    email: emailList,
    terms: z.enum(['prepay', 'deferred_7', 'deferred_14', 'deferred_30']),
    note: z.string().nullable(),
    groups: z.array(z.string().trim().min(1).max(200)).max(50),
    contacts: z.array(contactFields).max(100),
  })
  .strict()
export const supplierCreateSchema = supplierFields
  .extend({ groups: supplierFields.shape.groups.default([]) })
  .extend({ contacts: supplierFields.shape.contacts.default([]) })
  .strict()
// No defaults here: a default would turn a missing `groups` into an empty list and clear them.
export const supplierUpdateSchema = z
  .object({ version: warehouseVersion, patch: supplierFields.partial() })
  .strict()
export const supplierArchiveSchema = z
  .object({ version: warehouseVersion, archived: z.boolean() })
  .strict()

const supplierReference = z.object({ version: warehouseVersion }).strict()
export const supplierAddContactSchema = supplierReference
  .extend({ contact: contactFields })
  .strict()
export const supplierUpdateContactSchema = supplierReference
  .extend({
    personId: id,
    patch: contactFields
      .partial()
      .strict()
      .refine((patch) => Object.keys(patch).length > 0),
  })
  .strict()
export const supplierContactReferenceSchema = supplierReference.extend({ personId: id }).strict()
export const supplierGroupCreateSchema = z.object({ name: id }).strict()
export const supplierGroupUpdateSchema = z.object({ version: warehouseVersion, name: id }).strict()
export const supplierGroupArchiveSchema = z
  .object({ version: warehouseVersion, archived: z.boolean() })
  .strict()

export type SupplierContactInput = z.infer<typeof contactFields>
